import { useExchangeStore } from '@app/hooks/trade/exchangeStore.tsx';
import useTradeStore from '@app/hooks/trade/tradeStore.tsx';
import { IconEdit } from '@tabler/icons-react';
import { Button, Flex, Input, message, Slider } from 'antd';
import BigNumber from 'bignumber.js';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MText, MView } from '../../../../styles/mobilePageStyles';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import API from '../../../../utils/api';
import { COLORS } from '../../../constants';
import { useToken } from '../../../hooks';
import { useTradePosition } from '../../../hooks/trade/tradePosition';
import { useDisableScroll } from '@utils/useDisableScroll.tsx';
import throttle from 'lodash/throttle';
import { t } from 'i18next';
import { USER_LOG_TYPE, useUserLogger } from '@app/hooks/userLogger';
import { NEW_API_URL } from '@app/constants/APIKEYS';
import CBottomSheet from '@app/components/Common/CBottomSheet';
import * as S from './styles';

const TPSLEDIT = ({
  posid,
  price,
  leverage,
  amount,
  long_short,
  margin,
  tpsl,
  mark,
  liq,
  exchange_pairs_id,
  isMobile = false,
}: any) => {
  // console.log('TPSLEDIT', {
  //   posid,
  //   price,
  //   leverage,
  //   amount,
  //   long_short,
  //   margin,
  //   tpsl,
  //   mark,
  //   liq,
  //   exchange_pairs_id,
  //   isMobile,
  // });

  const { t } = useTranslation();
  const { data: token } = useToken();
  const [tp, setTp] = useState(tpsl?.tp);
  const [sl, setSl] = useState(tpsl?.sl);
  const [tpslAmount, setTpslAmount] = useState(0);
  const { mutate: mutatePosition } = useTradePosition();
  const { mutateUserLog, getTpSlLog } = useUserLogger();

  const [throttlePrice, setThrottlePrice] = useState(0);
  const [throttleMark, setThrottleMark] = useState(0);
  const [throttleLiq, setThrottleLiq] = useState(0);

  const TrPrice = useCallback(
    throttle((data) => {
      setThrottlePrice(data);
    }, 1000),
    []
  );

  const TrMark = useCallback(
    throttle((data) => {
      setThrottleMark(data);
    }, 1000),
    []
  );

  const TrLiq = useCallback(
    throttle((data) => {
      setThrottleLiq(data);
    }, 1000),
    []
  );

  useEffect(() => {
    TrPrice(price);
  }, [price]);

  useEffect(() => {
    TrMark(mark);
  }, [mark]);

  useEffect(() => {
    TrLiq(liq);
  }, [liq]);

  const [partialQuantity, setPartialQuantity] = useState('0');
  const [qtyPercentage, setQtyPercentage] = useState<any>(0);

  const { textw, gray, bgColor, mobileTradeBorder } =
    React.useContext(ThemeContext).theme.COLORS;

  const marks = {
    0: { style: { color: textw }, label: <strong>0%</strong> },
    25: { style: { color: textw }, label: <strong>25%</strong> },
    50: { style: { color: textw }, label: <strong>50%</strong> },
    75: { style: { color: textw }, label: <strong>75%</strong> },
    100: { style: { color: textw }, label: <strong>100%</strong> },
  };

  const { EPs } = useExchangeStore();

  const [position, setPosition] = useState<any>('entire'); // entire, partial

  const v = new BigNumber(price)
    .multipliedBy(amount)
    .multipliedBy(leverage)
    .toNumber();

  const calcpricelong = new BigNumber(long_short == 'L' ? tp : price)
    .minus(long_short == 'L' ? price : tp)
    .multipliedBy(amount)
    .multipliedBy(leverage);

  const calcpriceshort = new BigNumber(long_short == 'L' ? sl : price)
    .minus(long_short == 'L' ? price : sl)
    .multipliedBy(amount)
    .multipliedBy(leverage);

  const [open, setOpen] = useState(false);
  const [popoverKey, setPopoverKey] = useState(0);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  useEffect(() => {
    if (open) {
      // 팝업이 열릴 때마다 key를 업데이트하여 리렌더링 유도
      setPopoverKey((prevKey) => prevKey + 1);
    }
  }, [open]);

  const EP = EPs?.find((item2: any) => +item2.id == +exchange_pairs_id);

  useDisableScroll(open);

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  // 저장 핸들러
  const handleSave = () => {
    const base = (amount * leverage) / 100;
    const result = (base / qtyPercentage) * 100;

    if (tp || sl) {
      API.post(
        '/trade/tpsl',
        '' + token,
        {
          posId: posid,
          tp: tp,
          tptype: 'USDT',
          sl: sl,
          sltype: 'USDT',
          amount: position == 'entire' ? amount : result,
          amountType: 1,
        },
        NEW_API_URL
      ).then((res) => {
        mutateUserLog({
          log: getTpSlLog(
            {
              posId: posid,
              tp,
              sl,
            },
            USER_LOG_TYPE.TP_SL_ADD
          ),
          data: {
            posId: posid,
            tp,
            sl,
          },
        });
        handleOpenChange(false);
        message.success(`${t('success')}`);
        mutatePosition();
      });
    }
  };

  // 삭제 핸들러
  const handleDelete = () => {
    API.post(
      '/trade/tpsl',
      '' + token,
      {
        posId: posid,
        tp: 0,
        tptype: 'USDT',
        sl: 0,
        sltype: 'USDT',
        amount: amount,
        amountType: 1,
      },
      NEW_API_URL
    ).then((res) => {
      handleOpenChange(false);
      message.success(`${t('success')}`);
      mutateUserLog({
        log: getTpSlLog(
          {
            posId: posid,
            tp,
            sl,
          },
          USER_LOG_TYPE.TP_SL_DELETE
        ),
        data: {
          posId: posid,
        },
      });
      mutatePosition();
    });
  };

  // 공통 콘텐츠
  const renderTPSLContent = () => (
    <div onTouchMove={handleTouchMove}>
      {/* 가격 정보 영역 */}
      <S.PriceInfoBox>
        <S.PriceInfoItem>
          <S.PriceInfoLabel>{t('entryPrice')}</S.PriceInfoLabel>
          <S.PriceInfoValue>
            {BigNumber(throttlePrice).toFormat(EP?.dp || 3)}
          </S.PriceInfoValue>
        </S.PriceInfoItem>
        <S.PriceInfoItem>
          <S.PriceInfoLabel>{t('markPrice')}</S.PriceInfoLabel>
          <S.PriceInfoValue>
            {BigNumber(throttleMark).toFormat(EP?.dp || 3)}
          </S.PriceInfoValue>
        </S.PriceInfoItem>
        <S.PriceInfoItem>
          <S.PriceInfoLabel>{t('liqPrice')}</S.PriceInfoLabel>
          <S.PriceInfoValue>
            {BigNumber(throttleLiq).toFormat(EP?.dp || 3)}
          </S.PriceInfoValue>
        </S.PriceInfoItem>
      </S.PriceInfoBox>

      {/* TP/SL 입력 섹션 */}
      <S.TPSLSection>
        <CustomRow
          type={'tp'}
          price={price}
          amount={amount}
          leverage={leverage}
          margin={margin}
          long_short={long_short}
          defaultValue={tpsl?.tp}
          value={tp}
          setValue={setTp}
          isMobile={isMobile}
          positionType={position}
          dp={EP?.dp}
        />

        <CustomRow
          type={'sl'}
          price={price}
          amount={amount}
          leverage={leverage}
          margin={margin}
          long_short={long_short}
          defaultValue={tpsl?.sl}
          value={sl}
          setValue={setSl}
          isMobile={isMobile}
          positionType={position}
          dp={EP?.dp}
        />
      </S.TPSLSection>

      {/* 버튼 영역 */}
      <S.TPSLButtonContainer>
        <S.TPSLDeleteButton onClick={handleDelete}>
          {t('delete')}
        </S.TPSLDeleteButton>
        <S.TPSLSaveButton onClick={handleSave}>{t('save')}</S.TPSLSaveButton>
      </S.TPSLButtonContainer>
    </div>
  );

  return (
    <>
      {/* 모바일: BottomSheet, 데스크톱: Modal */}
      {isMobile ? (
        <CBottomSheet
          isOpen={open}
          onClose={() => handleOpenChange(false)}
          height="auto"
        >
          <S.BottomSheetHeader>
            <S.BottomSheetTitle>{t('TP/SL')}</S.BottomSheetTitle>
          </S.BottomSheetHeader>
          <S.BottomSheetBody>{renderTPSLContent()}</S.BottomSheetBody>
        </CBottomSheet>
      ) : (
        <S.TPSLModal
          key={popoverKey}
          open={open}
          title={t('TP/SL')}
          footer={null}
          className={'custom-modal'}
          onCancel={() => handleOpenChange(false)}
          centered
          styles={{
            header: {
              marginBottom: '16px',
            },
            body: {
              padding: '0',
            },
          }}
        >
          {renderTPSLContent()}
        </S.TPSLModal>
      )}

      <div
        style={{
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          gap: 6,
          paddingTop: 0,
        }}
        onClick={() => handleOpenChange(true)}
      >
        <S.TPSLEDITButton $isMobile={false} iconPosition={'end'} size={'small'}>
          {/*{isMobile ? 'TPSL' : ''}*/}
          {t('add')}
        </S.TPSLEDITButton>

        {/* <IconEdit size={13} color={textw} /> */}
      </div>
    </>
  );
};
export default React.memo(TPSLEDIT);

const calculateV = (price, amount, leverage) =>
  new BigNumber(price).multipliedBy(amount).multipliedBy(leverage).toNumber();

const calculateCalcPrice = (longShort, targetPrice, price, amount, leverage) =>
  new BigNumber(longShort === 'L' ? targetPrice : price)
    .minus(longShort === 'L' ? price : targetPrice)
    .multipliedBy(amount)
    .multipliedBy(leverage);

const calculateEstPercentage = (calcprice, v, leverage) =>
  calcprice.dividedBy(v).multipliedBy(leverage).multipliedBy(100).toFixed(2);

//ROI 계산 = (TP or SL - EP) / EP * 100 * leverage
const calcROI = (targetPrice, entryPrice, leverage) => {
  if (targetPrice == 0) {
    return 0;
  }

  const target = new BigNumber(targetPrice);
  const entry = new BigNumber(entryPrice);

  return target
    .minus(entry)
    .dividedBy(entry)
    .multipliedBy(100)
    .multipliedBy(leverage);
};

const calculateTargetPrice = (roiPercent, entryPrice, leverage) => {
  const roi = new BigNumber(roiPercent);
  const entry = new BigNumber(entryPrice);

  console.log('roi', roiPercent);
  console.log('entry', entryPrice);
  console.log('leverage', leverage);

  return entry.multipliedBy(
    new BigNumber(1).plus(
      roi.dividedBy(new BigNumber(100).multipliedBy(leverage))
    )
  );
};

const toNumber = (value) => parseFloat(value) || 0;
// toNumberFixed 함수 제거 - dp 기반으로 변경

const CustomRow = React.memo(
  (props: {
    type: 'tp' | 'sl';
    price?;
    amount?;
    leverage?;
    margin?;
    long_short?;
    defaultValue?;
    value?;
    setValue?;
    isMobile?: boolean;
    isSmall?: boolean;
    positionType?: 'entire' | 'partial';
    dp?: number;
  }) => {
    console.log('CustomRow', props);
    const { data: tradeStore, mutate: setTradeStore } = useTradeStore();

    const { textw, gray, bgColor } = useContext(ThemeContext).theme.COLORS;

    const marks = {
      0: { style: { color: textw }, label: <strong>0%</strong> },
      25: { style: { color: textw }, label: <strong>25%</strong> },
      50: { style: { color: textw }, label: <strong>50%</strong> },
      75: { style: { color: textw }, label: <strong>75%</strong> },
      100: { style: { color: textw }, label: <strong>100%</strong> },
    };

    const _price = toNumber(props.price); //Entry Price
    const _amount = toNumber(props.amount);
    const _leverage = toNumber(props.leverage);
    const _margin = toNumber(props.margin);
    const _tpslValue = toNumber(props.defaultValue);

    const marketPrice = toNumber(props.price);

    const [targetPrice, setTargetPrice] = useState<string | number>(
      _tpslValue === 0 ? '' : _tpslValue
    ); //tpsl 설정값

    const _roi = calcROI(_tpslValue, _price, _leverage).toFixed(2);

    // console.log('_tpslValue', _tpslValue);
    // console.log('_price', _price);
    // console.log('_roi', _roi);
    //
    // useEffect(() => {
    //   console.log('targetPrice', targetPrice)
    // }, [targetPrice])

    const [tpPercentage, setTpPercentage] = useState<any>(
      new BigNumber(_roi).abs()
    ); //ROI 슬라이드
    const [estPercentage, setEstPercentage] = useState<any>(_roi); //ROI 설정값

    const [partialQuantity, setPartialQuantity] = useState('0');
    const [qtyPercentage, setQtyPercentage] = useState<any>(0);

    const label =
      props.type === 'tp' ? t('TakeProfitSetting') : t('StopLossSetting');

    const smalllabel = props.type === 'tp' ? 'Take Profit' : 'Stop loss';

    const calcprice = calculateCalcPrice(
      props.long_short,
      targetPrice,
      props.price,
      props.amount,
      props.leverage
    );

    // console.log('calcprice', calcprice);
    //
    // const calculateTargetPrice = (
    //   percentage,
    //   long_short,
    //   price,
    //   amount,
    //   leverage,
    //   v,
    // ) => {
    //   const calcprice = new BigNumber(percentage)
    //     .dividedBy(100)
    //     .dividedBy(leverage)
    //     .multipliedBy(v);
    //   if (long_short === "L") {
    //     return new BigNumber(price)
    //       .plus(calcprice.dividedBy(amount).dividedBy(leverage))
    //       .toNumber();
    //   } else {
    //     return new BigNumber(price)
    //       .minus(calcprice.dividedBy(amount).dividedBy(leverage))
    //       .toNumber();
    //   }
    // };

    const handleTargetPriceChange = useCallback(
      (e) => {
        let price = e.target.value;

        // 빈 값이면 그대로 설정
        if (price === '' || price === undefined) {
          setTargetPrice('');
          props.setValue(0);
          setEstPercentage(0);
          setTpPercentage(0);
          return;
        }

        // 숫자로 변환
        const numPrice = parseFloat(price);

        // 유효하지 않은 숫자면 무시
        if (isNaN(numPrice)) {
          return;
        }

        // 소수점 자리수 검증 및 제한
        const dp = props.dp || 3;
        const priceStr = price.toString();
        const decimalIndex = priceStr.indexOf('.');

        if (decimalIndex !== -1) {
          const decimalPlaces = priceStr.length - decimalIndex - 1;
          if (decimalPlaces > dp) {
            // 소수점 자리수가 dp를 초과하면 잘라냄
            price = numPrice.toFixed(dp);
          }
        }

        setTargetPrice(price);

        //set tpsl - dp에 맞춰 포맷팅
        const formattedPrice = parseFloat(price).toFixed(dp);
        props.setValue(formattedPrice);

        const roi = calcROI(price, _price, _leverage).toFixed(2);
        setEstPercentage(roi);
        setTpPercentage(roi);
      },
      [_price, _leverage, props]
    );

    const handleROIChange = useCallback(
      (percentage) => {
        let adjustedPercentage;

        if (props.long_short === 'L') {
          if (props.type === 'sl') {
            adjustedPercentage = -percentage;
          } else {
            adjustedPercentage = percentage;
          }
        } else {
          if (props.type === 'sl') {
            adjustedPercentage = percentage;
          } else {
            adjustedPercentage = -percentage;
          }
        }

        const price = calculateTargetPrice(
          adjustedPercentage || 0,
          adjustedPercentage ? _price : 0,
          _leverage
        ).toFixed(props.dp || 3);

        // console.log('price11111', price);

        setTargetPrice(price);
        setTpPercentage(percentage);
        setEstPercentage(
          props.long_short === 'L' ? adjustedPercentage : -adjustedPercentage
        );
        props.setValue(price);
      },
      [_price, _leverage, props]
    );

    //slider
    const handleTpPercentageChange = useCallback(
      (percentage) => {
        console.log('props.long_short', props.long_short);
        let adjustedPercentage;

        if (props.long_short === 'L') {
          if (props.type === 'sl') {
            adjustedPercentage = -percentage;
          } else {
            adjustedPercentage = percentage;
          }
        } else {
          if (props.type === 'sl') {
            adjustedPercentage = percentage;
          } else {
            adjustedPercentage = -percentage;
          }
        }

        // const adjustedPercentage =
        //   // props.type === "sl" ? -percentage : percentage;
        //   props.type === "sl" && props.long_short === 'long' ? -percentage : percentage;

        const price = calculateTargetPrice(
          adjustedPercentage,
          _price,
          _leverage
        ).toFixed(props.dp || 5);

        setTargetPrice(price);
        setTpPercentage(percentage);
        setEstPercentage(
          props.long_short === 'L' ? adjustedPercentage : -adjustedPercentage
        );
        props.setValue(price);
      },
      [_price, _leverage, props]
    );

    const handleEstPercentageChange = useCallback(
      (percentage) => {
        const adjustedPercentage =
          props.type === 'sl' ? -percentage : percentage;

        const price = calculateTargetPrice(
          percentage,
          _price,
          _leverage
        ).toFixed(props.dp || 3);

        setTargetPrice(price);
        setTpPercentage(adjustedPercentage);
        setEstPercentage(adjustedPercentage);
        props.setValue(price);
      },
      [_price, _leverage, props]
    );

    const renderEntirePosition = () => (
      <S.TPSLInputGroup>
        <S.TPSLLabel>{props.isSmall ? smalllabel : label}</S.TPSLLabel>
        <S.TPSLInputRow>
          <S.TPSLInput $width="flex">
            <input
              type="number"
              value={targetPrice}
              onChange={handleTargetPriceChange}
              step={Math.pow(10, -(props.dp || 3)).toString()}
              placeholder={t('triggerPrice')}
            />
            <S.TPSLInputSuffix>USDT</S.TPSLInputSuffix>
          </S.TPSLInput>
          {!props.isSmall && (
            <S.TPSLInput $width="120px">
              <input
                type="number"
                value={tpPercentage}
                onChange={(e) => {
                  handleROIChange(e.target.value);
                }}
                placeholder="ROI"
              />
              <S.TPSLInputSuffix>%</S.TPSLInputSuffix>
            </S.TPSLInput>
          )}
        </S.TPSLInputRow>

        {/*<Slider*/}
        {/*  style={{*/}
        {/*    width: props.isSmall ? '93%' : '100%',*/}
        {/*    color: textw,*/}
        {/*    zIndex: 9999999,*/}
        {/*    position: 'relative',*/}
        {/*  }}*/}
        {/*  marks={marks}*/}
        {/*  value={tpPercentage}*/}
        {/*  onChange={handleTpPercentageChange}*/}
        {/*  min={1}*/}
        {/*  max={100}*/}
        {/*  styles={{*/}
        {/*    rail: { backgroundColor: gray },*/}
        {/*  }}*/}
        {/*/>*/}

        {/*<div style={{width: '100%'}}>*/}
        {/*  <PercentageForm percentage={tpPercentage} setPercentage={(v) => setTpPercentage(v)}/>*/}
        {/*</div>*/}

        <p
          style={{
            color: '#b3b5bd',
            fontSize: 12,
            lineHeight: props.isSmall ? 1.4 : 1.5,
            fontFamily: 'NanumSquareRound',
            letterSpacing: '-0.36px',
          }}
        >
          {t('LastTradedPriceTo')} {/*<span*/}
          {/*  style={{ color: props.type === "tp" ? "green" : "red" }}*/}
          {/*>{`${targetPrice} USDT`}</span>*/}
          <span
            style={{
              color: new BigNumber(targetPrice).isGreaterThanOrEqualTo(0)
                ? COLORS.lightGreen
                : COLORS.lightRed,
            }}
          >
            {`${targetPrice ? targetPrice : 0} USDT `}
          </span>
          {t('willTriggerMarketTakeProfitOrderYourExpectedProfitWillBe')}{' '}
          {/*<span style={{ color: props.type === "tp" ? "green" : "red" }}>*/}
          {/*  {calcprice.toFormat(2)}*/}
          {/*</span>*/}
          <span
            style={{
              color: calcprice.isGreaterThanOrEqualTo(0)
                ? COLORS.lightGreen
                : COLORS.lightRed,
            }}
          >
            {estPercentage === 0
              ? 0
              : calcprice.isFinite()
                ? calcprice.toFormat(2)
                : '0'}{' '}
          </span>
          (ROI:{' '}
          {/*<span style={{ color: props.type === "tp" ? "green" : "red" }}>*/}
          {/*  {estPercentage}%*/}
          {/*</span>*/}
          <span
            style={{
              color: new BigNumber(estPercentage).isGreaterThanOrEqualTo(0)
                ? COLORS.lightGreen
                : COLORS.lightRed,
            }}
          >
            {estPercentage}%
          </span>
          )
        </p>
        {/*<Flex*/}
        {/*	style={{ width: "100%" }}*/}
        {/*	align={"center"}*/}
        {/*	gap={props.isMobile ? "middle" : "small"}*/}
        {/*>*/}
        {/*	<MText style={{ color: textw, width: "7rem" }}>{pnlLabel}</MText>*/}
        {/*	<Input*/}
        {/*		readOnly*/}
        {/*		value={estValue}*/}
        {/*		style={{*/}
        {/*			background: bgColor,*/}
        {/*			minWidth: "50px",*/}
        {/*			width: "100%",*/}
        {/*			color:*/}
        {/*				calcprice.toNumber() > 0*/}
        {/*					? COLORS.lightGreen*/}
        {/*					: calcprice.toNumber() < 0*/}
        {/*						? COLORS.lightRed*/}
        {/*						: textw,*/}
        {/*			fontSize: 11,*/}
        {/*		}}*/}
        {/*	/>*/}
        {/*</Flex>*/}
      </S.TPSLInputGroup>
    );

    const renderPartialPosition = () => (
      <Flex vertical align={'start'} gap={props.isMobile ? 'middle' : 'small'}>
        <MText style={{ color: textw }}>{label}</MText>
        <Flex
          style={{ width: '100%' }}
          align={'center'}
          gap={props.isMobile ? 'middle' : 'small'}
        >
          <Input
            defaultValue={marketPrice.toFixed(2)}
            type="number"
            value={targetPrice}
            onChange={handleTargetPriceChange}
            size={props.isMobile ? 'middle' : 'small'}
            suffix={<div style={{ fontSize: 12, fontWeight: 600 }}>USDT</div>}
            style={{
              width: '50%',
              color: textw,
              backgroundColor: bgColor,
              padding: '2px 5px',
              borderRadius: 6,
            }}
          />

          <Input
            readOnly
            value={estPercentage}
            suffix={<div style={{ fontSize: 12, fontWeight: 600 }}>% ROI</div>}
            style={{
              background: bgColor,
              minWidth: '50px',
              width: '50%',
              color:
                calcprice.toNumber() > 0
                  ? COLORS.lightGreen
                  : calcprice.toNumber() < 0
                    ? COLORS.lightRed
                    : textw,
              fontSize: 12,
            }}
          />
        </Flex>
        <MText style={{ color: textw }}>{`Quantity`}</MText>
        <Flex style={{ width: '100%' }}>
          <Input
            // defaultValue={marketPrice.toFixed(2)}
            value={partialQuantity}
            // onChange={(e) => {
            //   setPartialQuantity(e.target.value);
            // }}
            size={props.isMobile ? 'middle' : 'small'}
            suffix={<div style={{ fontSize: 12, fontWeight: 600 }}>BTC</div>}
            style={{
              color: textw,
              backgroundColor: bgColor,
              padding: '2px 5px',
              borderRadius: 6,
            }}
          />
        </Flex>
        <Flex style={{ width: '100%' }}>
          <Slider
            style={{ width: '100%', color: textw }}
            marks={marks}
            defaultValue={qtyPercentage}
            value={Math.abs(qtyPercentage)}
            onChange={(e) => {
              const base = (props.amount * props.leverage) / 100;
              const result = base * e;
              const qty = `${result.toFixed(3)}(${e}%)`;
              setPartialQuantity(qty);
              setQtyPercentage(e);
            }}
            styles={{
              rail: { backgroundColor: gray },
            }}
          />
        </Flex>

        <p style={{ color: textw }}>
          Last Traded Price to{' '}
          <span
            style={{ color: props.type === 'tp' ? 'green' : 'red' }}
          >{`${targetPrice} USDT`}</span>{' '}
          will trigger market Take Profit order your expected profit will be{' '}
          <span style={{ color: props.type === 'tp' ? 'green' : 'red' }}>
            {calcprice.toFormat(2)}
          </span>
          (ROI:{' '}
          <span style={{ color: props.type === 'tp' ? 'green' : 'red' }}>
            {estPercentage}%
          </span>
          )
        </p>
      </Flex>
    );

    return renderEntirePosition();
    //
    // return props.positionType === "entire" ? (
    //   <EntirePosition />
    // ) : (
    //   <PartialPosition />
    // );
  }
);
